import React, { useMemo } from 'react';
import { Box, Grid } from '@mui/material';
import {
  People as PeopleIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  PersonAdd as PersonAddIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { UserMetrics, CHART_COLORS } from '../../types/dashboard.types';
import MetricCard from './MetricCard';
import ChartCard from './ChartCard';
import SectionHeader from './SectionHeader';
import EmptyState from './EmptyState';

export interface UserMetricsSectionProps {
  data: UserMetrics;
  loading?: boolean;
}

const UserMetricsSection: React.FC<UserMetricsSectionProps> = ({
  data,
  loading = false,
}) => {
  const verificationData = useMemo(
    () => [
      {
        name: 'Email Verified',
        value: data.emailVerificationRate,
        color: CHART_COLORS.success,
      },
      {
        name: 'Phone Verified',
        value: data.phoneVerificationRate,
        color: CHART_COLORS.primary,
      },
    ],
    [data.emailVerificationRate, data.phoneVerificationRate]
  );

  const countryData = useMemo(
    () =>
      data.usersByCountry.slice(0, 8).map((item, index) => ({
        name: item.country,
        count: item.count,
        fill: CHART_COLORS.palette[index % CHART_COLORS.palette.length],
      })),
    [data.usersByCountry]
  );

  const totalUsers = data.activeUsers + data.inactiveUsers;

  return (
    <Box>
      <SectionHeader title="Users & Verification" icon={<PeopleIcon />} />

      <Grid container spacing={3}>
        {/* Metric Cards Row */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Active Users"
            value={data.activeUsers}
            icon={<PeopleIcon />}
            color={CHART_COLORS.success}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Inactive Users"
            value={data.inactiveUsers}
            icon={<PeopleIcon />}
            color={CHART_COLORS.error}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="New This Week"
            value={data.newUsersThisWeek}
            icon={<PersonAddIcon />}
            color={CHART_COLORS.primary}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="New This Month"
            value={data.newUsersThisMonth}
            icon={<PersonAddIcon />}
            color={CHART_COLORS.purple}
            loading={loading}
          />
        </Grid>

        {/* Charts Row */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Verification Rates"
            subtitle="Email and phone verification percentages"
            height={280}
            loading={loading}
          >
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={verificationData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}%`}
                  labelLine={false}
                >
                  {verificationData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => [`${value}%`, 'Rate']}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 3,
                mt: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon sx={{ fontSize: 18, color: CHART_COLORS.success }} />
                <Box sx={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Email: {data.emailVerificationRate}%
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon sx={{ fontSize: 18, color: CHART_COLORS.primary }} />
                <Box sx={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Phone: {data.phoneVerificationRate}%
                </Box>
              </Box>
            </Box>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Users by Country"
            subtitle="Geographic distribution of users"
            height={280}
            loading={loading}
          >
            {countryData.length === 0 ? (
              <EmptyState
                message="No country data available"
                icon={<PublicIcon sx={{ fontSize: 32, color: '#9ca3af' }} />}
                height={250}
              />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={countryData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" stroke="#6b7280" />
                  <YAxis
                    dataKey="name"
                    type="category"
                    stroke="#6b7280"
                    width={100}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserMetricsSection;
